import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { CUSTOMER_QUERY } from "@/graphql/customers";
import { transformApolloError } from "@/lib/error/error-utils";
import { useSalonStore } from "@/lib/store/salon";

interface UseCustomerDetailQueryOptions {
  customerId: string;
}

export const useCustomerDetailQuery = ({ customerId }: UseCustomerDetailQueryOptions) => {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id || null;

  const {
    data,
    loading,
    error: apolloError,
    refetch,
  } = useQuery(CUSTOMER_QUERY, {
    variables: {
      customerId,
      salonId: salonId ?? "",
    },
    skip: !customerId || !salonId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const error = useMemo(() => {
    if (!apolloError) return null;
    return transformApolloError(apolloError);
  }, [apolloError]);

  const retry = () => {
    refetch();
  };

  return {
    customer: (data?.customer || null) as CustomerDetailFragment | null,
    loading,
    error,
    retry,
    refetch,
  };
};
